import { useState } from "react";
import Modal from "@/components/base/Modal";
import { createHomework, type TeacherStudent } from "@/hooks/useTeacher";
import { subjectOptions } from "@/data/panels";

interface HomeworkCreateModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  students: TeacherStudent[];
}

const inputClass =
  "w-full h-10 px-3 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none transition-colors";
const labelClass = "block text-sm font-semibold text-foreground-800 mb-1.5";

// Öğretmen, atanmış öğrencilerine toplu ödev oluşturur.
export default function HomeworkCreateModal({
  open,
  onClose,
  onCreated,
  students,
}: HomeworkCreateModalProps) {
  const [title, setTitle] = useState("");
  const [subject, setSubject] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [description, setDescription] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const toggle = (userId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) => {
      if (prev.size === students.length) return new Set();
      return new Set(students.map((s) => s.userId));
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");

    if (!title.trim() || !subject.trim() || !dueDate) {
      setError("Lütfen başlık, ders ve teslim tarihini doldurun.");
      return;
    }
    if (selected.size === 0) {
      setError("En az bir öğrenci seçin.");
      return;
    }

    setSaving(true);
    const res = await createHomework({
      studentIds: Array.from(selected),
      title: title.trim(),
      subject: subject.trim(),
      dueDate,
      description: description.trim(),
    });
    setSaving(false);

    if (res.ok) {
      setTitle("");
      setSubject("");
      setDueDate("");
      setDescription("");
      setSelected(new Set());
      onCreated();
      onClose();
    } else {
      setError(res.message || "Ödev oluşturulurken bir hata oluştu.");
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Ödev Oluştur"
      titleId="homework-create-title"
      id="homework-create-modal"
      maxWidthClass="max-w-lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="hw-title" className={labelClass}>
            Başlık <span className="text-accent-600">*</span>
          </label>
          <input
            id="hw-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Ör. Kesirler Alıştırma Ödevi"
            className={inputClass}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="hw-subject" className={labelClass}>
              Ders <span className="text-accent-600">*</span>
            </label>
            <select
              id="hw-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className={`${inputClass} cursor-pointer`}
            >
              <option value="" disabled>
                Ders seçin
              </option>
              {subjectOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="hw-due" className={labelClass}>
              Teslim Tarihi <span className="text-accent-600">*</span>
            </label>
            <input
              id="hw-due"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className={`${inputClass} cursor-pointer`}
            />
          </div>
        </div>

        <div>
          <label htmlFor="hw-desc" className={labelClass}>
            Açıklama
          </label>
          <textarea
            id="hw-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={500}
            rows={3}
            placeholder="Ödev açıklaması (isteğe bağlı)"
            className={`${inputClass} h-auto py-2 resize-none`}
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className={labelClass}>
              Öğrenciler <span className="text-accent-600">*</span>
            </span>
            <button
              type="button"
              onClick={toggleAll}
              className="text-xs font-semibold text-primary-700 hover:text-primary-800 whitespace-nowrap"
            >
              {selected.size === students.length ? "Tümünü Kaldır" : "Tümünü Seç"}
            </button>
          </div>
          {students.length === 0 ? (
            <p className="text-sm text-foreground-500">
              Henüz atanmış öğrencin yok. Önce "Öğrencilerim" sayfasından öğrenci ata.
            </p>
          ) : (
            <ul className="divide-y divide-background-200 max-h-[40svh] overflow-y-auto rounded-md border border-background-200">
              {students.map((s) => (
                <li key={s.userId} className="flex items-center gap-3 px-3 py-2.5">
                  <input
                    type="checkbox"
                    id={`hw-student-${s.userId}`}
                    checked={selected.has(s.userId)}
                    onChange={() => toggle(s.userId)}
                    className="w-4 h-4 rounded accent-[var(--primary-500)] cursor-pointer"
                  />
                  <label
                    htmlFor={`hw-student-${s.userId}`}
                    className="min-w-0 flex-1 text-sm text-foreground-800 cursor-pointer"
                  >
                    {s.fullName}
                    {s.grade ? <span className="text-foreground-500"> · {s.grade}</span> : ""}
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>

        {error && (
          <div className="rounded-md bg-accent-100/70 text-accent-800 px-3 py-2.5 text-sm">{error}</div>
        )}

        <div className="flex items-center gap-3 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 h-10 rounded-md border border-background-200 bg-background-50 text-foreground-700 text-sm font-semibold whitespace-nowrap hover:bg-background-100 transition-colors"
          >
            Vazgeç
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex-1 h-10 flex items-center justify-center gap-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? (
              <>
                <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
                Oluşturuluyor...
              </>
            ) : (
              "Ödevi Oluştur"
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}